import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Users, UserCheck, GraduationCap, BookOpen, MessageSquare, Settings, LogOut, Menu, ShieldCheck, Brain, Megaphone, ClipboardList, CreditCard, Clock, Layers, ShieldAlert, Share2, Inbox, Cpu, Calculator } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import NotificationsBell from "@/components/NotificationsBell";
import BrandLogo from "@/components/BrandLogo";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", perm: "dashboard", end: true },
  { to: "/admin/approvals", icon: Inbox, label: "Approvals Inbox", perm: "pending_student", badgeKey: "approvals" },
  { to: "/admin/sub-admins", icon: ShieldCheck, label: "Sub-Admin", perm: "sub_admin", superOnly: true },
  { to: "/admin/pending", icon: UserCheck, label: "Pending Students", perm: "pending_student" },
  { to: "/admin/students", icon: Users, label: "Student List", perm: "student_list" },
  { to: "/admin/temporary-access", icon: Clock, label: "Temporary Access Students", perm: "temporary_access_students" },
  { to: "/admin/courses", icon: BookOpen, label: "Courses", perm: "course" },
  { to: "/admin/abstract-exam", icon: Layers, label: "Abstract Exam", perm: "abstract_exam", superOnly: true, fallbackPerm: "course" },
  { to: "/admin/enrollment-requests", icon: ClipboardList, label: "Enrollment Requests", perm: "enrollment_request" },
  { to: "/admin/payments", icon: CreditCard, label: "Payments", perm: "enrollment_request" },
  { to: "/admin/accounting", icon: Calculator, label: "Accounting", perm: "enrollment_request" },
  { to: "/admin/learning-monitor", icon: Brain, label: "AI Learning Monitor", perm: "ai_learning_monitor" },
  { to: "/admin/broadcasts", icon: Megaphone, label: "Broadcasts", perm: "push_broadcast" },
  { to: "/admin/ai-config", icon: Cpu, label: "AI Model Manager", perm: "settings" },
  { to: "/admin/share-analytics", icon: Share2, label: "Share Analytics", perm: "share_analytics" },
  { to: "/admin/support", icon: MessageSquare, label: "Support", perm: "support" },
  { to: "/admin/reports", icon: ShieldAlert, label: "Reports", modOrSuper: true },
  { to: "/admin/settings", icon: Settings, label: "Settings", perm: "settings" },
];

function Sidebar({ onNav }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [approvalsCount, setApprovalsCount] = useState(0);
  // Poll a rolled-up count for the Approvals Inbox red pill — refreshes every
  // 60s so the sidebar reflects new pending items without a manual reload.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      const canPending = hasPerm(user, "pending_student", "view");
      const canReq = hasPerm(user, "enrollment_request", "view");
      if (!canPending && !canReq) return;
      try {
        const [regs, enr, pay] = await Promise.all([
          canPending ? api.get("/users/pending").then((r) => (r.data || []).length).catch(() => 0) : Promise.resolve(0),
          canReq ? api.get("/admin/enrollment-requests?status=pending").then((r) => r.data?.counts?.pending || 0).catch(() => 0) : Promise.resolve(0),
          canReq ? api.get("/admin/payments?status=pending_verification").then((r) => r.data?.counts?.pending_verification || 0).catch(() => 0) : Promise.resolve(0),
        ]);
        if (!cancelled) setApprovalsCount(regs + enr + pay);
      } catch { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);
  const items = navItems.filter((it) => {
    if (it.superOnly && user.role !== "super_admin") return false;
    // Moderation pages: visible to super_admin AND the new `moderator` role.
    // Moderators have no per-module permissions doc, so we bypass `hasPerm`.
    if (it.modOrSuper) return ["super_admin", "moderator"].includes(user.role);
    return hasPerm(user, it.perm, "view");
  });
  return (
    <aside className="h-full w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col">
      <div className="h-16 px-4 flex items-center border-b border-slate-200">
        <BrandLogo size="md" />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNav}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-50"
              }`
            }
            data-testid={`nav-${it.perm || it.label.toLowerCase().replace(/\s+/g,'-')}`}
          >
            <it.icon className="w-4 h-4" strokeWidth={1.5} />
            <span className="flex-1">{it.label}</span>
            {it.badgeKey === "approvals" && approvalsCount > 0 && (
              <span
                className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold tabular-nums shadow-sm"
                data-testid="admin-nav-approvals-count"
                title={`${approvalsCount} pending item${approvalsCount === 1 ? "" : "s"}`}
              >
                {approvalsCount > 99 ? "99+" : approvalsCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="flex items-center justify-between gap-2 px-3 mb-2">
          <div className="text-xs text-slate-500 truncate">{user.full_name}</div>
          <div className="flex items-center gap-1">
            <NotificationsBell testId="admin-desktop-bell-btn" />
            <ThemeToggle />
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-slate-700" onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>
    </aside>
  );
}

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-[#F8FAFC] dark:bg-slate-950">
      <div className="hidden lg:block"><Sidebar /></div>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden h-14 px-4 flex items-center justify-between border-b border-slate-200 bg-white">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu /></Button></SheetTrigger>
            <SheetContent side="left" className="p-0 w-64"><Sidebar onNav={() => setOpen(false)} /></SheetContent>
          </Sheet>
          <div className="font-display font-bold flex items-center"><BrandLogo size="sm" /></div>
          <div className="flex items-center gap-1">
            <NotificationsBell testId="admin-mobile-bell-btn" />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
